import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { intelligenceApi, type ClientSiteProfileView, type SitePageView, type SitesReadResult } from '../api/intelligence'
import { formatDateBR, formatDateTimeBR } from '../lib/format'
import { Spinner } from './Spinner'

const money = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
const display = (url: string) => url.replace(/^https?:\/\//, '').replace(/\/$/, '')

/**
 * SITE E PÁGINAS DE DESTINO — o que a IA leu de cada site do cliente (perfil do negócio,
 * relido todo mês) e as páginas que recebem o dinheiro do Google (até 8) mais as fixadas no
 * cadastro, checadas todo dia: no ar ou não, o que oferecem e quando mudaram. Tudo isso entra
 * na análise semanal.
 */
export function ClientSite({ clientId }: { clientId: string }) {
  const qc = useQueryClient()
  const queryKey = ['client-site', clientId]
  const { data, isLoading } = useQuery({ queryKey, queryFn: () => intelligenceApi.site(clientId) })
  const refresh = useMutation({
    mutationFn: () => intelligenceApi.refreshSite(clientId),
    onSuccess: (res) => {
      qc.setQueryData(queryKey, res)
      qc.invalidateQueries({ queryKey: ['client-insights', clientId] })
      qc.invalidateQueries({ queryKey: ['intelligence-inbox'] })
    },
  })

  if (isLoading) return <Spinner />
  const pages = data?.pages ?? []
  const sites = data?.sites ?? []
  const problems = pages.filter((p) => p.problem)

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        A IA não navega na internet: ela lê o que buscamos aqui. As páginas que mais recebem cliques do Google (até 8)
        e as fixadas no cadastro são checadas <strong>todo dia</strong> (no ar? o que oferecem? mudou?) e cada site é
        relido todo mês. O que os sites dizem do negócio vira <strong>proposta</strong> no dossiê, para você confirmar.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
          title="Busca as páginas de destino dos últimos 30 dias, checa cada uma (as fixadas também) e relê cada site inteiro."
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {refresh.isPending && <Spinner className="h-3.5 w-3.5" />}
          {refresh.isPending
            ? `Lendo ${sites.length > 1 ? 'os sites' : 'o site'} e as páginas (até ${sites.length > 1 ? 3 : 2} min)…`
            : sites.length > 1
              ? 'Ler os sites agora'
              : 'Ler o site agora'}
        </button>
        {refresh.isSuccess && refresh.data.result && (
          <span className={`text-xs ${refresh.data.result.ran ? 'text-emerald-700' : 'text-amber-700'}`}>
            {refreshMessage(refresh.data.result)}
          </span>
        )}
        {refresh.isError && <span className="text-xs text-red-600">Falhou: {String((refresh.error as Error).message)}</span>}
      </div>

      {problems.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <strong>⚠️ {problems.length} página(s) com problema</strong> — os anúncios estão mandando gente para elas:
          <ul className="mt-1 list-disc pl-5 text-xs">
            {problems.map((p) => (
              <li key={p.id}>
                {display(p.url)}: {p.problem}
                {p.problemSince ? ` (desde ${formatDateBR(p.problemSince)})` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
        <div className="mb-1 text-xs font-semibold text-slate-600">
          {sites.length > 1 ? `O que os ${sites.length} sites dizem do negócio` : 'O que o site diz do negócio'}
        </div>
        {sites.length === 0 ? (
          <p className="text-sm text-slate-500">
            Nenhum site informado no cadastro e nenhum detectado ainda — a IA usa os domínios que recebem os cliques do
            Google. Informe os sites na aba Dados.
          </p>
        ) : (
          <ul className="divide-y divide-slate-200">
            {sites.map((s) => (
              <SiteItem key={s.id} site={s} />
            ))}
          </ul>
        )}
      </div>

      {pages.length === 0 ? (
        <p className="text-sm text-slate-500">
          Nenhuma página monitorada ainda (vêm das páginas de destino do Google dos últimos 30 dias e das fixadas no
          cadastro, em Páginas para monitorar).
        </p>
      ) : (
        <ul className="space-y-2">
          {pages.map((p) => (
            <PageItem key={p.id} page={p} />
          ))}
        </ul>
      )}
    </div>
  )
}

/** O que aconteceu no "Ler agora": quantos sites foram lidos, os fatos e os que não deu para ler. */
function refreshMessage(r: SitesReadResult): string {
  if (!r.ran) return r.reason
  const read = r.sites.filter((s) => s.ran).length
  const notRead = r.sites.filter((s) => !s.ran)
  return (
    `${r.sites.length > 1 ? `${read} de ${r.sites.length} sites lidos` : 'Lido'}. ${r.facts} fato(s) proposto(s) ao dossiê.` +
    (notRead.length ? ` Não lido: ${notRead.map((s) => `${s.label || display(s.home ?? s.url)} (${s.reason})`).join('; ')}.` : '')
  )
}

function SiteItem({ site: s }: { site: ClientSiteProfileView }) {
  // A home lida pode ser outra (redirecionamento, ou o domínio principal de uma LP).
  const home = s.home ?? s.url
  return (
    <li className="py-2 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-baseline gap-x-1.5 text-xs">
        {s.label && <span className="font-semibold text-slate-700">{s.label}</span>}
        <a
          href={home}
          target="_blank"
          rel="noreferrer"
          className={`text-brand-700 hover:underline ${s.label ? '' : 'font-semibold'}`}
        >
          {display(home)}
        </a>
        {s.auto && <span className="text-slate-400">· detectado pelos anúncios do Google</span>}
        {s.profileAt && <span className="text-slate-400">· lido em {formatDateBR(s.profileAt)}</span>}
      </div>
      <p className={`mt-0.5 text-sm ${s.profile ? 'text-slate-700' : 'text-slate-500'}`}>
        {s.profile ?? 'Ainda não lido — acontece na próxima rodada diária ou pelo botão acima.'}
      </p>
    </li>
  )
}

function PageItem({ page: p }: { page: SitePageView }) {
  const cvr = p.clicks30d > 0 ? (p.conv30d / p.clicks30d) * 100 : null
  const s = p.signals
  const yes = (v: boolean | null | undefined) => (v == null ? '?' : v ? 'sim' : 'não')
  const recentChange = p.changedAt && Date.now() - new Date(p.changedAt).getTime() < 30 * 86400_000
  return (
    <li className={`rounded-lg border bg-white p-3 ${p.problem ? 'border-red-300' : 'border-slate-200'}`}>
      <div className="flex flex-wrap items-center gap-2">
        <a href={p.url} target="_blank" rel="noreferrer" className="text-sm font-medium text-brand-700 hover:underline">
          {display(p.url)}
        </a>
        {p.problem ? (
          <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">Com problema</span>
        ) : p.checkedAt ? (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">No ar</span>
        ) : (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">Ainda não checada</span>
        )}
        {p.pinned && (
          <span
            className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-700"
            title="Fixada no cadastro (Páginas para monitorar): checada todo dia mesmo sem cliques do Google."
          >
            Fixada
          </span>
        )}
        {p.via === 'reader' && (
          <span className="text-[11px] text-slate-400" title="Página montada por JavaScript: lida por um leitor externo.">
            lida por leitor
          </span>
        )}
        {p.via === 'blocked' && <span className="text-[11px] text-amber-600">o site bloqueia leitura automática</span>}
      </div>
      <p className="mt-0.5 text-xs text-slate-500">
        {p.pinned && !(p.spend30d > 0) ? (
          '30 dias: sem cliques do Google'
        ) : (
          <>
            30 dias: {money(p.spend30d)} · {Math.round(p.clicks30d)} cliques ·{' '}
            {p.conv30d.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} conv.
            {cvr != null ? ` · ${cvr.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% de conversão` : ''}
          </>
        )}
        {p.checkedAt ? ` · checada ${formatDateTimeBR(p.checkedAt)}` : ''}
      </p>
      {p.problem && (
        <p className="mt-1 text-sm text-red-700">
          {p.problem}
          {p.problemSince ? ` — desde ${formatDateBR(p.problemSince)}` : ''}
          {p.okAt ? ` (última vez no ar: ${formatDateBR(p.okAt)})` : ''}
        </p>
      )}
      {p.summary && <p className="mt-1 text-sm text-slate-700">{p.summary}</p>}
      {s && (
        <p className="mt-1 text-xs text-slate-500">
          WhatsApp {yes(s.whatsapp)} · formulário {yes(s.formulario)} · telefone {yes(s.telefone)}
          {s.precos?.length ? ` · preços: ${s.precos.join(', ')}` : ''}
        </p>
      )}
      {recentChange && p.changeSummary && (
        <p className="mt-1 rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-800">
          Mudou em {formatDateBR(p.changedAt)}: {p.changeSummary}
        </p>
      )}
    </li>
  )
}
